import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { getCompanySettings, selectCompanySettings, selectCompanySettingsState } from '../../store/entities/companySettings';
import { Box, Divider, Typography } from '@mui/material';
import { ConditionalTimeInput } from '../common/form/ConditionalTimeInput';
import { QcsSwitch } from '../common/form/QcsSwitch';
import { CompanySettingsDto } from '@api/models/settings/CompanySettingsDto';
import { DeletionConfirmationModal } from './DeletionConfirmationModal';
import { TimeInputOutput } from '@api/models/settings/TimeInputOutput';
import { companySettingsService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { Input } from '../common/form/Input';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';

export const CompSettTabGeneral: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const companySettings = useAppSelector(selectCompanySettings);
  const businessSettings = useBusinessSettings();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingData, setPendingData] = useState<CompanySettingsDto | null>(null);
  const initialValuesRef = useRef<CompanySettingsDto>(companySettings);

  useEffect(() => {
    dispatch(getCompanySettings());
  }, [dispatch, identityCompanyId]);

  useEffect(() => {
    // Update initial values ref when company settings are loaded
    initialValuesRef.current = companySettings;
  }, [companySettings]);

  const checkIfDeletionEnabled = (initial: TimeInputOutput | undefined, current: TimeInputOutput | null | undefined): boolean => {
    // Check if value changed from null/undefined to a non-null value
    if (initial == null && current != null) {
      return true;
    }
    // Check if value or unit changed when both are defined
    if (initial != null && current != null) {
      return initial.value !== current.value || initial.unit !== current.unit;
    }
    return false;
  };

  const performSubmit = async (data: CompanySettingsDto) => {
    await companySettingsService.updateSettings(data);
    dispatch(getCompanySettings());
  };

  const handleSubmit = async (data: CompanySettingsDto) => {
    const initial = initialValuesRef.current;

    // Check if any deletion settings were enabled (changed from null to non-null)
    const entryEnabled = checkIfDeletionEnabled(initial.entryRecordsDeletion, data.entryRecordsDeletion);
    const trainingEnabled = checkIfDeletionEnabled(initial.trainingDeletion, data.trainingDeletion);
    const certificateEnabled = checkIfDeletionEnabled(initial.certificateDeletion, data.certificateDeletion);

    const needsConfirmation = entryEnabled || trainingEnabled || certificateEnabled;

    if (needsConfirmation) {
      setPendingData(data);
      setShowConfirmationModal(true);
      return;
    }
    await performSubmit(data);
  };

  const handleConfirmDeletion = async () => {
    setShowConfirmationModal(false);
    if (pendingData) {
      await performSubmit(pendingData);
      setPendingData(null);
      // Update initial values to the new confirmed values
      initialValuesRef.current = pendingData;
    }
  };

  const handleCancelDeletion = () => {
    setShowConfirmationModal(false);
    setPendingData(null);
  };

  return (
    <Box mt={2}>
      <ShowResourceContent resource={resources.getSettings} fetchStateSelector={selectCompanySettingsState}>
        <FormikDebounce<CompanySettingsDto>
          initialValues={companySettings}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={Yup.object().shape({
            internalGdprLink: validations.urlOptional(t),
          })}
        >
          <Form>
            <FormContainer>
              <Typography variant="h5" fontWeight={600} mb={1}>
                {t('qcsCompanySettings.invitations')}
              </Typography>

              <ConditionalTimeInput
                fieldName="earliestEntryTimeTolerance"
                titleKey="qcsCompanySettings.earliestEntryTimeTolerance"
                timeInputLabelKey="qcsCompanySettings.earliestEntryTimeToleranceLabel"
                outputUnit="minutes"
                defaultUnit="minutes"
                outputFormat="number"
              />
              <ConditionalTimeInput
                fieldName="archiveInvalidInvitationsAfter"
                titleKey="qcsCompanySettings.archiveInvalidInvitationsAfter"
                timeInputLabelKey="qcsCompanySettings.archiveInvalidInvitationsAfterLabel"
                outputUnit="days"
                defaultUnit="days"
                outputFormat="number"
              />

              <Typography variant="h6" fontWeight={600} mb={1} mt={1}>
                {t('qcsCompanySettings.defaultValues')}
              </Typography>

              <QcsSwitch name="defaultAddWorkerToInvitation" label={t('qcsCompanySettings.defaultAddWorkerToInvitation')} />

              <QcsSwitch name="defaultDisableVisitationNotification" label={t('qcsCompanySettings.defaultDisableVisitationNotification')} />

              <QcsSwitch name="defaultNotifyEmployees" label={t('qcsCompanySettings.defaultNotifyEmployees')} />

              {businessSettings?.workplaceHandoverEnabled && (
                <QcsSwitch name="defaultCreateHandoverProtocol" label={t('qcsCompanySettings.defaultCreateHandoverProtocol')} />
              )}

              {businessSettings?.turnstilesEnabled && (
                <QcsSwitch name="defaultTurnstilesActive" label={t('qcsCompanySettings.defaultTurnstilesActive')} />
              )}

              {businessSettings?.allowEntryRequests && (
                <QcsSwitch name="defaultEntryRequestRequired" label={t('qcsCompanySettings.defaultEntryRequestRequired')} />
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h5" fontWeight={600} mb={1}>
                {t('qcsCompanySettings.gdpr')}
              </Typography>

              <Input name="internalGdprLink" label={t('qcsCompanySettings.internalGdprLink')} />

              <Typography variant="h6" fontWeight={600} mb={3} mt={1}>
                {t('qcsCompanySettings.recordsDeletionSettings')}
              </Typography>

              <ConditionalTimeInput
                fieldName="entryRecordsDeletion"
                titleKey="qcsCompanySettings.entryRecordsDeletion.title"
                timeInputLabelKey="qcsCompanySettings.deletionPeriodLabel"
                defaultUnit="months"
                outputFormat={'object'}
              />

              <ConditionalTimeInput
                fieldName="trainingDeletion"
                titleKey="qcsCompanySettings.trainingRecordsDeletion.title"
                timeInputLabelKey="qcsCompanySettings.deletionPeriodLabel"
                defaultUnit="months"
                outputFormat={'object'}
              />

              <ConditionalTimeInput
                fieldName="certificateDeletion"
                titleKey="qcsCompanySettings.certificateRecordsDeletion.title"
                timeInputLabelKey="qcsCompanySettings.deletionPeriodLabel"
                defaultUnit="months"
                outputFormat={'object'}
              />

              <ShowError resource={resources.updateSettings} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>

      <DeletionConfirmationModal
        open={showConfirmationModal}
        entryRecordsDeletion={pendingData?.entryRecordsDeletion}
        trainingDeletion={pendingData?.trainingDeletion}
        certificateDeletion={pendingData?.certificateDeletion}
        onConfirm={handleConfirmDeletion}
        onCancel={handleCancelDeletion}
      />
    </Box>
  );
};
